import { TableRow } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Blueprint } from 'types';

type BlueprintCalculation = Blueprint['calculations'][number];

/**
 * Calculates savings percentage between base and hybrid values
 * @param baseValue - Base calculation value (can be null/undefined)
 * @param hybridValue - Hybrid calculation value (can be null/undefined)
 * @returns Percentage string (e.g., "-25%", "+20%", "-") or empty string if data is missing
 */
export const calculateSavingsPercentage = (
  baseValue: number | null | undefined,
  hybridValue: number | null | undefined
) => {
  if (
    baseValue === null ||
    baseValue === undefined ||
    hybridValue === null ||
    hybridValue === undefined ||
    baseValue === 0
  ) {
    return ''; // Empty string for savings when data is missing or base value is zero
  }
  const savings = baseValue - hybridValue;
  const percentage = ((savings / baseValue) * 100).toFixed(0);
  const percentageValue = parseFloat(percentage);

  // Zero savings should show "0%"
  if (percentageValue === 0) {
    return '0%';
  }

  // Positive savings (reduction) get minus sign, negative savings (increase) get plus sign
  const sign = percentageValue > 0 ? '-' : '+';
  const absolutePercentage = Math.abs(percentageValue);
  return `${sign}${absolutePercentage}%`;
};

/**
 * Checks if savings percentage value is positive (represents actual savings/reduction)
 * @param savings - Savings percentage string (e.g., "-25%", "+20%", "0%")
 * @returns true if percentage represents savings (has minus sign), false otherwise
 */
export const isPositiveSavings = (savings: string) => {
  if (!savings || savings === '-' || savings === '0%') {
    return false;
  }
  // Positive savings now have minus sign (e.g., "-25%")
  return savings.startsWith('-');
};

/**
 * Maps blueprint calculation data to table data format
 * @param calculation - Blueprint calculation object
 * @param overrides - Optional overrides for costAssumptions fields
 * @returns Formatted data for table generation
 */
export const mapCalculationToTableData = (
  calculation: BlueprintCalculation,
  overrides?: { batteryRentalCostCents?: number }
) => {
  return {
    currencyCode: calculation.currencyCode,
    calculationResult: {
      generatorUsageHours: calculation.calculationResult.generatorUsageHours,
      dieselCostCents: calculation.calculationResult.dieselCostCents,
      gridElectricityCostCents: calculation.calculationResult.gridElectricityCostCents,
      totalCostEstimateCents: calculation.calculationResult.totalCostEstimateCents,
      emissionsCo2Tons: calculation.calculationResult.emissionsCo2Tons,
      emissionsNoxKg: calculation.calculationResult.emissionsNoxKg,
      rentalCostCents: calculation.calculationResult.rentalCostCents,
      additionalCostCents: calculation.calculationResult.additionalCostCents,
    },
    costAssumptions: {
      gensetRentalCostCents: calculation.costAssumptions.gensetRentalCostCents,
      batteryRentalCostCents: overrides?.batteryRentalCostCents ?? calculation.costAssumptions.batteryRentalCostCents,
      additionalCostsCents: calculation.costAssumptions.additionalCostsCents,
    },
  };
};

/**
 * Styled component for the last row with background and rounded corners
 */
export const StyledLastRow = styled(TableRow)(({ theme }) => ({
  '& td': {
    borderRadius: '6px',
    background: theme.palette.background.default,
    border: 'none',
  },
  '& td:first-of-type': {
    borderRadius: '6px 0 0 6px',
  },
  '& td:last-of-type': {
    borderRadius: '0 6px 6px 0',
  },
}));
